import create from 'zustand'
import { combine } from 'zustand/middleware'
import { OptionsObject, enqueueSnackbar } from '@ui/snackbar'

/**
 * Opt-in of a block action to also refuse leaving the drawer pane of the current selection
 * (another element, mode or tab, ESC past the sub-selection, a storey or 2D/3D switch), with the
 * snackbar that explains why.
 */
export interface PaneBlock {
  message: string
  options?: OptionsObject
}

interface BlockActionStoreInitialState {
  /** Actions currently blocked by a `useBlockAction` provider (exactly one provider per action). */
  blockedActions: Set<string>
  /** The blocked actions that also refuse leaving the pane, see `PaneBlock`. */
  paneBlocks: Map<string, PaneBlock>
}

const initialState: BlockActionStoreInitialState = {
  blockedActions: new Set(),
  paneBlocks: new Map(),
}

/**
 * Global singleton (like the selection store) rather than a context store, so the selection store
 * and the control store can refuse transitions while an action is blocked. Providers remove their
 * block on unmount, so the store empties itself when the editor unmounts.
 */
const useBlockActionStore = create(
  combine({ ...initialState }, set => ({
    addBlockedAction: (action: string, paneBlock?: PaneBlock) =>
      set(state => {
        // A given action must have exactly one provider. Two providers for the same action
        // would corrupt the block: one unmounting removes the block while the other still
        // needs it. Fail loudly so the duplicate provider is caught during development.
        if (state.blockedActions.has(action)) {
          throw new Error(
            `Block action "${action}" is already registered. Only one useBlockAction provider may exist per action.`,
          )
        }

        const paneBlocks = new Map(state.paneBlocks)
        if (paneBlock) paneBlocks.set(action, paneBlock)

        return { blockedActions: new Set(state.blockedActions).add(action), paneBlocks }
      }),

    removeBlockedAction: (action: string) =>
      set(state => {
        const blockedActions = new Set(state.blockedActions)
        blockedActions.delete(action)
        const paneBlocks = new Map(state.paneBlocks)
        paneBlocks.delete(action)

        return { blockedActions, paneBlocks }
      }),
  })),
)

/**
 * Whether a blocked action refuses leaving the current pane. When it does, it shows that action's
 * snackbar (only the first one, so the user gets one explanation) and returns `true`; the caller
 * must then drop the transition.
 */
const refusePaneChange = (): boolean => {
  const [paneBlock] = useBlockActionStore.getState().paneBlocks.values()
  if (!paneBlock) return false

  enqueueSnackbar(paneBlock.message, paneBlock.options)
  return true
}

export { useBlockActionStore, refusePaneChange }
